import {
  AGENT_DATASETS, AGENT_LANGUAGES, AGENT_MODELS, AGENT_SURFACES, AGENT_TONES, AgentConfig, DEFAULT_AGENT_CONFIG,
  type AgentConfigT, type AgentInputT, type AgentSurfaceT, type AgentT, type ConversationAgent, type ResolvedAgent,
} from "@malfak/contracts";
import type { TFunction } from "i18next";

/** Pure helpers for the Agents screens (form ↔ contract, picker values, labels). Tested in model.test.ts. */

export { AGENT_DATASETS, AGENT_LANGUAGES, AGENT_MODELS, AGENT_SURFACES, AGENT_TONES };
export const AGENT_EMOJIS = ["🤖", "🛍️", "🎧", "💬", "🌸", "✨", "🧕", "🧑‍💼", "🎨", "📦", "💡", "🦉", "🌙", "⭐"] as const;
export const SNIPPET_KEYS = ["scope", "prices", "dm", "complaints"] as const;
export type SnippetKey = (typeof SNIPPET_KEYS)[number];

/** The editor's form state: the agent minus server fields. */
export type AgentForm = {
  name: string;
  emoji: string | null;
  description: string;
  status: "active" | "paused";
  mode: "draft_for_approval" | "auto_send";
  config: AgentConfigT;
};

export function emptyForm(): AgentForm {
  return { name: "", emoji: "🤖", description: "", status: "active", mode: "draft_for_approval", config: structuredClone(DEFAULT_AGENT_CONFIG) };
}

export function toForm(a: AgentT): AgentForm {
  // normalise through the schema so older/partial configs get every default the form edits
  return { name: a.name, emoji: a.emoji, description: a.description, status: a.status, mode: a.mode, config: AgentConfig.parse(a.config) };
}

export function toInput(f: AgentForm, version?: number, note?: string): AgentInputT {
  return {
    name: f.name.trim(), emoji: f.emoji, description: f.description.trim(), status: f.status, mode: f.mode, config: f.config,
    ...(version !== undefined ? { version } : {}), ...(note?.trim() ? { note: note.trim() } : {}),
  };
}

/** Problems that block saving (shown inline). */
export function formErrors(f: AgentForm): { name?: "required" } {
  return f.name.trim() ? {} : { name: "required" };
}

export const sameForm = (a: AgentForm, b: AgentForm) => JSON.stringify(a) === JSON.stringify(b);

/** Insert an instruction snippet on its own line (no duplicate). */
export function insertSnippet(instructions: string, snippet: string): string {
  if (instructions.includes(snippet)) return instructions;
  const base = instructions.trimEnd();
  return (base ? `${base}\n${snippet}` : snippet).slice(0, 8000);
}

// ---------------------------------------------------------------- conversation picker
/** The picker's value: "off", "inherit" or an agent id. */
export type PickerValue = "off" | "inherit" | (string & {});
export function pickerValue(a: ConversationAgent | undefined, legacyOn?: boolean): PickerValue {
  if (!a) return legacyOn === false ? "off" : "inherit";
  if (a.mode === "off") return "off";
  if (a.mode === "assigned" && a.agentId) return a.agentId;
  return "inherit";
}
export function pickerBody(v: PickerValue): { mode: "inherit" | "assigned" | "off"; agentId: string | null } {
  if (v === "off") return { mode: "off", agentId: null };
  if (v === "inherit") return { mode: "inherit", agentId: null };
  return { mode: "assigned", agentId: v };
}

/** "from account default", "from rule Price questions", "assigned to this conversation". */
export function sourceText(r: Pick<ResolvedAgent, "source" | "sourceLabel">, t: TFunction): string {
  switch (r.source) {
    case "rule":
      return t("agents.sources.rule", { label: r.sourceLabel ?? "" }).trim();
    case "conversation":
      return t("agents.sources.conversation");
    case "post":
      return t("agents.sources.post");
    case "account":
      return t("agents.sources.account");
    case "channel":
      return t("agents.sources.channel", { label: r.sourceLabel ?? t("agents.assignments.instagram") });
  }
}
export const agentLabel = (r: Pick<ResolvedAgent, "name" | "emoji">) => `${r.emoji ? `${r.emoji} ` : ""}${r.name}`;

/** "This conversation is handled by: 🛍️ Sales agent (from account default)", or the off / nobody line. */
export function handledByText(a: ConversationAgent | undefined, t: TFunction): string {
  if (a?.mode === "off") return t("agents.picker.offHint");
  if (!a?.resolved) return t("agents.picker.nobody");
  return t("agents.picker.handledBy", { agent: agentLabel(a.resolved), source: sourceText(a.resolved, t) });
}

// ---------------------------------------------------------------- usage / misc
/** 0..1 share of a limit used (null limit = no bar). */
export function ratio(used: number, limit: number | null): number | null {
  if (limit === null || limit <= 0) return null;
  return Math.min(1, used / limit);
}
export const defaultSurfaceFor = (type: "comment" | "conversation"): AgentSurfaceT => (type === "conversation" ? "dm" : "comment_public");
/** Weekday names in the UI language (0 = Sunday). */
export function weekdayName(day: number, lang: string, style: "short" | "long" = "short"): string {
  const d = new Date(Date.UTC(2026, 0, 4 + day)); // 2026-01-04 is a Sunday
  return new Intl.DateTimeFormat(lang, { weekday: style, timeZone: "UTC" }).format(d);
}
/** A few useful zones first, then the browser's. */
export function timezones(current?: string): string[] {
  const local = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  })();
  return [...new Set([current, local, "Asia/Amman", "Asia/Riyadh", "Asia/Dubai", "Asia/Kuwait", "Asia/Qatar", "Africa/Cairo", "Africa/Casablanca", "Europe/London", "UTC"].filter((z): z is string => !!z))];
}
